import React from 'react'
import moment from 'moment'
import { useListState } from '@mantine/hooks'
import { Link, useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { IconCubePlus, IconEdit, IconExternalLink } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ActionIcon, Badge, Button, Group, List, Paper, Text, Title } from '@mantine/core'

import { PageProps } from '@customTypes/page'
import { ProformaBaseType, GetProformasType } from '@customTypes/proforma'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { useIsMobile, useTableMinWidth } from '@hooks/mobile'

import QueryKeys from '@constants/queryKeys'
import { RoutesApp } from '@constants/routes'
import { DATE_FORMAT, UI } from '@constants/app'
import { StatusProforma } from '@constants/proforma'

import { ErrorService } from '@utils/error'
import { onFilterProforma } from '@utils/proforma'
import { isLoadingMutation, isLoadingOrRefetchQuery } from '@utils/network'

import { useParams } from '@hooks/params'

import ConfirmationModal from '@components/shared/confirmationModal'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import Cont from '@components/shared/cont'

const Proforma = (props: PageProps) => {
  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile)

  const { cardPreview } = props

  const [modalDelete, setModalDelete] = React.useState<ProformaBaseType[] | null>(null)

  const [params, setParams] = useParams<GetProformasType>({
    queryKey: QueryKeys.GET_PROFORMAS_KEY,
    initialParams: {},
  })

  const [selectedRows, handlers] = useListState<string>([])

  const proformasQuery = useQuery<ProformaBaseType[], ErrorService, ProformaBaseType[]>({
    queryKey: [QueryKeys.GET_PROFORMAS_KEY],
    queryFn: async () => {
      const response = await DataRepo.documentEntitiesService.getProformas({})

      return response
    },
  })

  const proformasDeleteMutation = useMutation<void, ErrorService, string[]>({
    mutationFn: async (uids) => {
      const response = await DataRepo.documentEntitiesService.deleteProformas(uids)

      setParams({})

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_PROFORMAS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar la proforma',
        })
      }

      setModalDelete(null)
      handlers.setState([])

      return notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'La(s) proforma(s) han sido eliminadas correctamente',
      })
    },
  })

  const memoFilterProforma = React.useMemo(
    () =>
      onFilterProforma({
        data: proformasQuery.data,
        params,
        limit: cardPreview ? 5 : undefined,
      }),
    [proformasQuery.data, params, cardPreview],
  )

  React.useEffect(() => {
    return () => {
      handlers.setState([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoading = isLoadingOrRefetchQuery(proformasQuery)

  if (cardPreview) {
    return (
      <React.Fragment>
        <Paper
          withBorder
          className="cd-flex cd-flex-col cd-gap-y-[1rem]"
          p="lg"
          radius="md"
          shadow="md"
        >
          <Link
            className="hover:cd-text-blue-400 cd-cursor-pointer cd-w-fit cd-flex cd-flex-row cd-gap-x-[0.5rem] cd-items-center"
            to={RoutesApp.PROFORMAS}
          >
            <Title order={3}>Proformas</Title>
            <IconExternalLink size={20} />
          </Link>
          <TableCustom<ProformaBaseType>
            columns={buildColumns()}
            data={memoFilterProforma}
            hidePagination={cardPreview}
            keyId="uid"
            loading={isLoading}
            loadingMessage="Cargando proformas"
            minWidth={minWidth}
            noDataMessage="No se encontraron proformas"
            onDetail={({ uid }) => {
              navigate(RoutesApp.PROFORMA_VIEW.replace(':uid', uid))
            }}
          />
        </Paper>
      </React.Fragment>
    )
  }

  return (
    <Cont>
      <div className="cd-flex cd-flex-col cd-gap-y-[2rem]">
        <div className="cd-flex cd-flex-col md:cd-flex-row cd-gap-[1rem] cd-grow">
          <Text className="cd-text-lg md:cd-basis-[80%]">Proformas</Text>
          <Button
            fullWidth
            className="md:cd-basis-[20%]"
            color="green"
            leftSection={<IconCubePlus size={18} />}
            size={UI.Size}
            variant="filled"
            onClick={() => {
              navigate(RoutesApp.PROFORMA_FORM.replace('/:uid', ''))
            }}
          >
            Crear
          </Button>
        </div>

        <TableCustom<ProformaBaseType>
          allData={proformasQuery.data}
          columns={buildColumns()}
          data={memoFilterProforma}
          defaultColumnSearch="name"
          keyId="uid"
          loading={isLoading}
          loadingMessage="Cargando proformas"
          minWidth={minWidth}
          noDataMessage="No se encontraron proformas"
          selectedRows={selectedRows}
          onChangeSelected={handlers}
          onDelete={(ids) => {
            const data = proformasQuery.data?.filter((item) => ids.includes(item.uid))

            if (!data?.length) return

            setModalDelete(data)
          }}
          onDetail={({ uid }) => {
            navigate(RoutesApp.PROFORMA_VIEW.replace(':uid', uid))
          }}
          onSearch={(query, field, startDate, endDate) =>
            setParams({ query, field, startDate, endDate })
          }
        />
      </div>

      <ConfirmationModal
        cancelColor="gray"
        cancelText="Cancelar"
        confirmColor="red"
        confirmText="Eliminar"
        loading={isLoadingMutation(proformasDeleteMutation)}
        open={Boolean(modalDelete?.length)}
        title="Eliminar proforma"
        onCancel={() => setModalDelete(null)}
        onConfirm={() => {
          if (!modalDelete) return

          proformasDeleteMutation.mutate(modalDelete.map((item) => item.uid))
        }}
      >
        {modalDelete?.length === 1 && (
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar la proforma{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {modalDelete[0].uid} - {modalDelete[0].name}
            </Text>
            ?
          </Text>
        )}
        {modalDelete && modalDelete?.length > 1 && (
          <React.Fragment>
            <Text className="cd-text-base">
              ¿Estás seguro que deseas eliminar las siguientes proformas?
            </Text>
            <List withPadding className="cd-mt-[0.5rem] cd-list-disc" type="unordered">
              {modalDelete.map((item) => (
                <List.Item key={item.uid}>
                  <Text className="cd-text-base">
                    {item.uid} - {item.name}
                  </Text>
                </List.Item>
              ))}
            </List>
          </React.Fragment>
        )}
      </ConfirmationModal>
    </Cont>
  )

  function buildColumns() {
    return [
      {
        key: 'sequential',
        label: 'Nro.',
        width: '10%',
        type: 'text',
        align: 'right',
        searchable: true,
      },
      { key: 'name', label: 'Nombre', width: '30%', type: 'text', searchable: true },
      {
        key: 'status',
        label: 'Estado',
        width: '15%',
        type: 'calc',
        searchable: true,
        defaultOnClick: true,
        render: ({ status }) => (
          <div className="cd-text-center">
            <Badge color={StatusProforma[status].color}>{StatusProforma[status].label}</Badge>
          </div>
        ),
      },
      {
        key: 'client',
        label: 'Cliente',
        width: '20%',
        searchable: true,
        defaultOnClick: true,
        type: 'calc',
        render: ({ client }) => client.name,
      },
      {
        key: 'date',
        label: 'Fecha',
        width: '15%',
        type: 'calc',
        searchable: true,
        defaultOnClick: true,
        render: ({ createdAt }) => moment.unix(createdAt).format(DATE_FORMAT),
      },
      {
        key: 'actions',
        label: 'Acciones',
        width: '5%',
        type: 'calc',
        align: 'center',
        render: ({ uid }) => (
          <Group justify="center">
            <ActionIcon
              variant="transparent"
              onClick={() => {
                navigate(RoutesApp.PROFORMA_FORM.replace(':uid', uid))
              }}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Group>
        ),
      },
    ] as GenericColumnType<ProformaBaseType>[]
  }
}

export default Proforma
